import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
} from "recharts";

type TrendPoint = {
  period: string;
  count: number;
};

type PlanItem = {
  plan_slug: string;
  billing_status: string;
  count: number;
};

const CHART_COLORS = ["#2d6a4f", "#40916c", "#52b788", "#74c69d", "#95d5b2", "#b7e4c7"];

function formatChartPeriod(period: string): string {
  const weekMatch = period.match(/^(\d{4})-W(\d{2})$/);
  if (weekMatch) {
    return `W${weekMatch[2]}`;
  }
  return period;
}

function withFormattedPeriods(data: TrendPoint[]): Array<TrendPoint & { label: string }> {
  return data.map((point) => ({
    ...point,
    label: formatChartPeriod(point.period),
  }));
}

type LineTrendProps = {
  title: string;
  data: TrendPoint[];
};

export function LineTrendChart({ title, data }: LineTrendProps) {
  const chartData = withFormattedPeriods(data);

  return (
    <article className="admin-chart">
      <h3 className="admin-chart__title">{title}</h3>
      {chartData.length === 0 ? (
        <p className="muted admin-chart__empty">Brak danych w wybranym okresie.</p>
      ) : (
        <div className="admin-chart__canvas">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={12}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
              <Tooltip labelFormatter={(_, payload) => payload?.[0]?.payload?.period ?? ""} />
              <Line type="monotone" dataKey="count" stroke="#2d6a4f" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </article>
  );
}

type BarTrendProps = {
  title: string;
  data: TrendPoint[];
};

export function BarTrendChart({ title, data }: BarTrendProps) {
  const chartData = withFormattedPeriods(data);

  return (
    <article className="admin-chart">
      <h3 className="admin-chart__title">{title}</h3>
      {chartData.length === 0 ? (
        <p className="muted admin-chart__empty">Brak danych w wybranym okresie.</p>
      ) : (
        <div className="admin-chart__canvas">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={12}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
              <Tooltip labelFormatter={(_, payload) => payload?.[0]?.payload?.period ?? ""} />
              <Bar dataKey="count" fill="#40916c" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </article>
  );
}

type PlanChartProps = {
  title: string;
  data: PlanItem[];
};

export function PlanDistributionChart({ title, data }: PlanChartProps) {
  const chartData = data.map((item) => ({
    name: `${item.plan_slug} (${item.billing_status})`,
    value: item.count,
  }));

  return (
    <article className="admin-chart admin-chart--plans">
      <h3 className="admin-chart__title">{title}</h3>
      {chartData.length === 0 ? (
        <p className="muted admin-chart__empty">Brak danych.</p>
      ) : (
        <div className="admin-chart__canvas admin-chart__canvas--pie">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="45%"
                outerRadius="72%"
                label={false}
              >
                {chartData.map((_, index) => (
                  <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: "0.85rem" }} />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </article>
  );
}
